import { z } from 'zod';

import { defineNode, passThrough, total } from '../define';

const REEL_IN = [{ id: 'in', accepts: ['reel'] }] as const;
const REEL_OUT = [{ id: 'out', type: 'reel' }] as const;

export const TRANSLATE_LANGUAGES = [
  'es',
  'de',
  'fr',
  'it',
  'pt',
  'nl',
  'pl',
  'ja',
] as const;
export const ASPECT_RATIOS = ['9:16', '1:1', '4:5', '16:9'] as const;

export const hookStitch = defineNode({
  type: 'edit.hookStitch',
  version: 1,
  category: 'edit',
  label: 'Hook stitch',
  description: 'Split a reel into hook and body so either can be swapped.',
  icon: 'Scissors',
  status: 'ga',
  inputs: REEL_IN,
  outputs: [
    { id: 'hook', label: 'Hook', type: 'clip' },
    { id: 'body', label: 'Body', type: 'clip' },
  ],
  mode: 'map',
  executor: 'render',
  rendersPerItem: 1,
  config: z.object({
    hookSeconds: z.number().min(1).max(10).default(3).meta({ title: 'Hook length (s)' }),
  }),
  cardinality: (_config, inputs) => ({ hook: total(inputs), body: total(inputs) }),
});

export const merge = defineNode({
  type: 'edit.merge',
  version: 1,
  category: 'edit',
  label: 'Merge',
  description: 'Combine every hook × body into finished reels.',
  icon: 'GitMerge',
  status: 'ga',
  inputs: [
    { id: 'hook', label: 'Hook', accepts: ['clip'] },
    { id: 'body', label: 'Body', accepts: ['clip'] },
  ],
  outputs: REEL_OUT,
  mode: 'collect',
  executor: 'render',
  rendersPerItem: 1,
  config: z.object({}),
  cardinality: (_config, inputs) => ({ out: (inputs.hook ?? 0) * (inputs.body ?? 0) }),
});

export const textOverlay = defineNode({
  type: 'edit.textOverlay',
  version: 1,
  category: 'edit',
  label: 'Text overlay',
  description: 'Burn on-screen text in your brand style.',
  icon: 'TextT',
  status: 'ga',
  inputs: REEL_IN,
  outputs: REEL_OUT,
  mode: 'map',
  executor: 'render',
  rendersPerItem: 1,
  config: z.object({
    /** Empty uses the script's hook line. */
    text: z.string().trim().max(120).optional().meta({ title: 'Text' }),
    position: z.enum(['top', 'center', 'bottom']).default('top').meta({
      title: 'Position',
      widget: 'segmented',
    }),
  }),
  cardinality: passThrough,
});

export const subtitles = defineNode({
  type: 'edit.subtitles',
  version: 1,
  category: 'edit',
  label: 'Subtitles',
  description: 'Word-by-word captions styled to your brand kit.',
  icon: 'ClosedCaptioning',
  status: 'ga',
  inputs: REEL_IN,
  outputs: REEL_OUT,
  mode: 'map',
  executor: 'render',
  rendersPerItem: 1,
  config: z.object({
    style: z.enum(['wordByWord', 'line']).default('wordByWord').meta({
      title: 'Style',
      widget: 'segmented',
    }),
  }),
  cardinality: passThrough,
});

export const removeCaptions = defineNode({
  type: 'edit.removeCaptions',
  version: 1,
  category: 'edit',
  label: 'Remove captions',
  description: 'Erase burned-in text from a reference before remixing.',
  icon: 'Eraser',
  status: 'ga',
  inputs: [{ id: 'in', accepts: ['clip'] }],
  outputs: [{ id: 'out', type: 'clip' }],
  mode: 'map',
  executor: 'render',
  rendersPerItem: 1,
  config: z.object({}),
  cardinality: passThrough,
});

export const translate = defineNode({
  type: 'edit.translate',
  version: 1,
  category: 'edit',
  label: 'Translate',
  description: 'Localise speech and on-screen text into other languages.',
  icon: 'Translate',
  status: 'ga',
  inputs: REEL_IN,
  outputs: REEL_OUT,
  mode: 'map',
  executor: 'render',
  rendersPerItem: 1,
  config: z.object({
    languages: z
      .array(z.enum(TRANSLATE_LANGUAGES))
      .min(1, 'Pick at least one language')
      .default(['es', 'de'])
      .meta({ title: 'Languages', widget: 'chips' }),
  }),
  cardinality: (config, inputs) => ({ out: total(inputs) * config.languages.length }),
});

export const reformat = defineNode({
  type: 'edit.reformat',
  version: 1,
  category: 'edit',
  label: 'Reformat',
  description: 'Resize per platform: 9:16, 1:1, 4:5.',
  icon: 'Crop',
  status: 'ga',
  inputs: REEL_IN,
  outputs: REEL_OUT,
  mode: 'map',
  executor: 'render',
  rendersPerItem: 1,
  config: z.object({
    aspectRatios: z
      .array(z.enum(ASPECT_RATIOS))
      .min(1, 'Pick at least one size')
      .default(['9:16'])
      .meta({ title: 'Sizes', widget: 'chips' }),
  }),
  cardinality: (config, inputs) => ({ out: total(inputs) * config.aspectRatios.length }),
});

export const trimPace = defineNode({
  type: 'edit.trimPace',
  version: 1,
  category: 'edit',
  label: 'Trim & pace',
  description: 'Hit a target length; cut silences and dead air.',
  icon: 'Timer',
  status: 'ga',
  inputs: REEL_IN,
  outputs: REEL_OUT,
  mode: 'map',
  executor: 'render',
  rendersPerItem: 1,
  config: z.object({
    targetSeconds: z
      .int()
      .min(5)
      .max(180)
      .default(30)
      .meta({ title: 'Target length (s)' }),
    cutSilences: z.boolean().default(true).meta({ title: 'Cut silences' }),
  }),
  cardinality: passThrough,
});

export const brandKit = defineNode({
  type: 'edit.brandKit',
  version: 1,
  category: 'edit',
  label: 'Brand kit',
  description: 'Logo, end card, fonts and colours from your brand profile.',
  icon: 'Palette',
  status: 'ga',
  inputs: REEL_IN,
  outputs: REEL_OUT,
  mode: 'map',
  executor: 'render',
  rendersPerItem: 1,
  config: z.object({
    logo: z.boolean().default(true).meta({ title: 'Logo' }),
    endCard: z.boolean().default(true).meta({ title: 'End card' }),
  }),
  cardinality: passThrough,
});

export const remixMetadata = defineNode({
  type: 'edit.remixMetadata',
  version: 1,
  category: 'edit',
  label: 'Remix metadata',
  description: 'Fresh file fingerprint on re-encode.',
  icon: 'Fingerprint',
  status: 'soon',
  inputs: REEL_IN,
  outputs: REEL_OUT,
  mode: 'map',
  executor: 'render',
  config: z.object({}),
  cardinality: passThrough,
});

export const EDIT = [
  hookStitch,
  merge,
  textOverlay,
  subtitles,
  removeCaptions,
  translate,
  reformat,
  trimPace,
  brandKit,
  remixMetadata,
];
